import { useQueryClient } from '@tanstack/react-query';
import React, { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

interface Props {
  departmentId: number;
  comment?: string;
  onClose: () => void;
}

type FormDataT = {
  comment?: string;
};

const FreelancerCommentModal: FC<Props> = ({
  departmentId,
  comment,
  onClose,
}) => {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const {
    handleSubmit,
    setError,
    clearErrors,

    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      comment: comment,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.post<FormDataT, any>(
          `/v1/payments/freelancers/department/store-wages-comment`,
          {
            freelancer_department_details_id: departmentId,
            ...data,
          },
        );

        await queryClient.invalidateQueries(['freelancers_details', { id }]);

        alerts.success(`Freelancer's wages comment added succesfully`);

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, false);
      }
    })(e);
  };

  return (
    <Modal stopPropagation={false}>
      <SidePanel
        heading={`${comment ? 'Edit' : 'Add'} wages comment`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField
                label="Comment"
                control={control}
                name="comment"
                type="textarea"
                placeholder="Add comment"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {comment ? ' Update' : 'Create'}
            </Button>
            <Button
              color="secondary"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClose();
              }}
            >
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default FreelancerCommentModal;
